import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface IInlineMarkdownProps {
  /** Single-line or short multi-line text. Treated as markdown. */
  text: string;
}

/**
 * Inline markdown renderer for short LLM-generated strings (summary
 * bullets, risk callouts, prereqs, test-plan steps, operator notes).
 * Differences from `ReleaseBodyMarkdown`:
 *
 * - No block-level chrome — `<p>` is collapsed to a fragment so the
 *   text flows inline within whatever list/cell wraps it.
 * - Bare URLs auto-linkify via `remark-gfm`.
 * - Links open in a new tab and adopt the brand accent.
 * - `` `code` `` spans render in mono with a subtle background so they
 *   read as code without looking like a separate block.
 *
 * To get the most out of this, the upstream LLM prompt should be told
 * to wrap env vars / feature flag names / file paths in backticks and
 * to format references as proper markdown links — without that, you
 * still get auto-linkified bare URLs and any incidental backticks.
 */
const InlineMarkdown = ({ text }: IInlineMarkdownProps) => (
  <Markdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <>{children}</>,
      a: ({ href, children, ...props }) => (
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
          {...props}
        >
          {children}
        </a>
      ),
      code: ({ children, ...props }) => (
        // Orange (`fg-warning-primary`) so identifiers don't visually clash
        // with teal links. The bg + border are literal hex (raw `gray-dark-800`
        // and `gray-dark-600` palette stops) because the `bg-bg-*` /
        // `border-border-*` semantic tokens don't materialize in dark mode
        // in this build — they fall back to `currentColor`, which would
        // make the entire chip read as orange.
        <code
          className="text-fg-warning-primary rounded-sm border border-[#61646c] bg-[#22262f] px-1 py-0.5 font-mono text-[0.85em] font-medium"
          {...props}
        >
          {children}
        </code>
      ),
    }}
  >
    {text}
  </Markdown>
);

export default InlineMarkdown;
